/**
 * @file Parses `parameters/analytics` into `AnalyticsParameters`, refusing with
 * `AnalyticsNotConfigured` and the dotted key at the first problem: a required key missing or
 * malformed, or a key unknown at any level. Optional keys never refuse the document: an absent
 * `openItems` means `all`, an absent `reconciliationClosedStates` means `complete` and an absent
 * `teamBusinessGroups` means no team, the only code defaults. A malformed optional key is dropped,
 * never defaulted, so it refuses only the queries that depend on it. Pure and shared from
 * `types/src`: `readAnalyticsParameters` in the analytics service hands it the stored document.
 */
import { IANAZone } from 'luxon';
import { ANALYTICS_WEEKDAYS } from './Analytics.Catalog.types';
import type { AnalyticsCalendar } from './Analytics.Catalog.types';
import { ANALYTICS_OPEN_ITEMS, ANALYTICS_PARAMETERS_PATH, ANALYTICS_PERIOD_DATES } from './Analytics.Parameters.types';
import type { AnalyticsOpenItems, AnalyticsParameters } from './Analytics.Parameters.types';
import { AnalyticsNotConfigured } from './AnalyticsNotConfigured';

const FIRST_MONTH = 1;
const LAST_MONTH = 12;
const DEFAULT_OPEN_ITEMS: AnalyticsOpenItems = 'all';
const DEFAULT_RECONCILIATION_CLOSED_STATES = ['complete'];
const DEFAULT_TEAM_BUSINESS_GROUPS: string[] = [];

const keyed = (
    value: unknown,
    key: string,
    keys: readonly string[],
    optionalKeys: readonly string[] = [],
): Record<string, unknown> => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new AnalyticsNotConfigured(key);
    const entries = Object.fromEntries(Object.entries(value));
    const prefix = key === ANALYTICS_PARAMETERS_PATH ? '' : `${key}.`;
    const unknownKey = Object.keys(entries).find((entry) => !keys.includes(entry) && !optionalKeys.includes(entry));
    if (unknownKey !== undefined) throw new AnalyticsNotConfigured(`${prefix}${unknownKey}`);
    const missingKey = keys.find((entry) => !(entry in entries));
    if (missingKey !== undefined) throw new AnalyticsNotConfigured(`${prefix}${missingKey}`);
    return entries;
};

const parseCalendar = (value: unknown): AnalyticsCalendar => {
    const { timeZone, fiscalYearStartMonth, weekStartsOn } = keyed(value, 'calendar', [
        'timeZone',
        'fiscalYearStartMonth',
        'weekStartsOn',
    ]);
    if (typeof timeZone !== 'string' || !IANAZone.isValidZone(timeZone)) {
        throw new AnalyticsNotConfigured('calendar.timeZone');
    }
    if (
        typeof fiscalYearStartMonth !== 'number' ||
        !Number.isInteger(fiscalYearStartMonth) ||
        fiscalYearStartMonth < FIRST_MONTH ||
        fiscalYearStartMonth > LAST_MONTH
    ) {
        throw new AnalyticsNotConfigured('calendar.fiscalYearStartMonth');
    }
    const weekday = ANALYTICS_WEEKDAYS.find((day) => day === weekStartsOn);
    if (weekday === undefined) throw new AnalyticsNotConfigured('calendar.weekStartsOn');
    return { timeZone, fiscalYearStartMonth, weekStartsOn: weekday };
};

const valueList = (value: unknown): string[] | undefined => {
    const values = Array.isArray(value)
        ? value.flatMap((entry: unknown) => (typeof entry === 'string' && entry !== '' ? [entry] : []))
        : [];
    return Array.isArray(value) && values.length > 0 && values.length === value.length ? values : undefined;
};

const idList = (value: unknown): string[] | undefined =>
    (Array.isArray(value) && value.length === 0 ? [] : valueList(value));

const fieldId = (value: unknown): string | undefined => (typeof value === 'string' && value !== '' ? value : undefined);

const parseValueList = (value: unknown, key: string): string[] => {
    const values = valueList(value);
    if (values === undefined) throw new AnalyticsNotConfigured(key);
    return values;
};

export function parseAnalyticsParameters(data: unknown): AnalyticsParameters {
    const document = keyed(
        data,
        ANALYTICS_PARAMETERS_PATH,
        ['calendar', 'stages', 'quoteClosedStates', 'lineItems'],
        ['openItems', 'reconciliationClosedStates', 'teamBusinessGroups', 'formDueDateField'],
    );
    const parsedCalendar = parseCalendar(document.calendar);
    const stages = keyed(document.stages, 'stages', ['order', 'quote', 'buy'], ['reconciliation']);
    const reconciliation = valueList(stages.reconciliation);
    const parsedStages = {
        order: parseValueList(stages.order, 'stages.order'),
        quote: parseValueList(stages.quote, 'stages.quote'),
        buy: parseValueList(stages.buy, 'stages.buy'),
        ...(reconciliation === undefined ? {} : { reconciliation }),
    };
    const parsedClosedStates = parseValueList(document.quoteClosedStates, 'quoteClosedStates');
    const reconciliationClosedStates =
        'reconciliationClosedStates' in document
            ? valueList(document.reconciliationClosedStates)
            : [...DEFAULT_RECONCILIATION_CLOSED_STATES];
    const openItems =
        'openItems' in document
            ? ANALYTICS_OPEN_ITEMS.find((value) => value === document.openItems)
            : DEFAULT_OPEN_ITEMS;
    const teamBusinessGroups =
        'teamBusinessGroups' in document
            ? idList(document.teamBusinessGroups)
            : [...DEFAULT_TEAM_BUSINESS_GROUPS];
    const formDueDateField = fieldId(document.formDueDateField);
    const lineItems = keyed(
        document.lineItems,
        'lineItems',
        ['held', 'released', 'lighting', 'controls', 'periodDate'],
        ['shipped', 'fulfilled'],
    );
    const shipped = valueList(lineItems.shipped);
    const fulfilled = valueList(lineItems.fulfilled);
    const parsedLineItems = {
        held: parseValueList(lineItems.held, 'lineItems.held'),
        released: parseValueList(lineItems.released, 'lineItems.released'),
        lighting: parseValueList(lineItems.lighting, 'lineItems.lighting'),
        controls: parseValueList(lineItems.controls, 'lineItems.controls'),
        ...(shipped === undefined ? {} : { shipped }),
        ...(fulfilled === undefined ? {} : { fulfilled }),
    };
    const parsedPeriodDate = ANALYTICS_PERIOD_DATES.find((date) => date === lineItems.periodDate);
    if (parsedPeriodDate === undefined) throw new AnalyticsNotConfigured('lineItems.periodDate');
    return {
        calendar: parsedCalendar,
        stages: parsedStages,
        quoteClosedStates: parsedClosedStates,
        ...(reconciliationClosedStates === undefined ? {} : { reconciliationClosedStates }),
        ...(openItems === undefined ? {} : { openItems }),
        ...(teamBusinessGroups === undefined ? {} : { teamBusinessGroups }),
        ...(formDueDateField === undefined ? {} : { formDueDateField }),
        lineItems: { ...parsedLineItems, periodDate: parsedPeriodDate },
    };
}
